import React, {Component} from "react";
import TopNav from "../../../common/nav/TopNav";
import Footer from "../../../common/Footer";
import PageBlock from "../../../common/PageBlock";
import Link from "react-router/lib/Link";
import CodeBlock from "../../../common/CodeBlock";

class API extends Component {
    render() {
        return (
            <div>
                <TopNav tab="docs"/>

                <PageBlock>
                    <div className="container">

                        <div className="row">
                            <div className="col-lg-12">
                                <h1 className="page-header">CloudMocks API</h1>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-sm-12">
                                <h3>
                                    Introduction
                                </h3>
                                <p>
                                    You can upload, update and manage your mocks using either our API or the <Link to="/mocks/config">Web Console</Link>.
                                </p>
                                <p>
                                    Using the API enables you to integrate the deployment of mocks into your CI/CD pipeline
                                    or other automated development/testing process.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-sm-12">
                                <h4>
                                    Supported operations
                                </h4>
                                <p>
                                    The following operations are supported:
                                </p>
                                <ul>
                                    <li>create new mock</li>
                                    <li>upload file</li>
                                    <li>provision mock</li>
                                    <li>get mock status</li>
                                    <li>get mock endpoint</li>
                                    <li>get provisioning events</li>
                                    <li>list all mocks</li>
                                    <li>list uploaded files</li>
                                </ul>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-sm-12">
                                <h3>
                                    Concepts
                                </h3>
                                <p>
                                    The API is accessible over HTTPS.
                                    All requests must be authenticated with an <Link to="/account">API Key</Link>.
                                </p>
                                <p>
                                    When a mock is first created it is in <code>DRAFT</code> status.
                                    After creation, you upload your OpenAPI specifications and other files.
                                    To activate your mock you <em>provision</em> it.
                                </p>
                                <h4>
                                    Authentication
                                </h4>
                                <p>
                                    All requests must be authenticated with an <Link to="/account">API Key</Link>. This is passed in the
                                    <code>Authorization</code> HTTP header, using the <code>Bearer</code> type.
                                </p>
                                <CodeBlock className="dark-code-block" code={`Authorization: Bearer abc|x82uh92h3r932hnffiejwe0ds`}/>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-sm-12">
                                <h3>
                                    APIs
                                </h3>
                                <p>
                                    In the following examples, we assume you have set the environment variable <code>CLOUDMOCKS_KEY</code> as follows:
                                </p>
                                <CodeBlock className="m-t-sm dark-code-block" code={`
export CLOUDMOCKS_KEY="abc|x82uh92h3r932hnffiejwe0ds"
                                    `}/>

                                <h4 className="m-t-md">Create new mock</h4>
                                <p>
                                    This API creates a new mock, and returns its ID.
                                </p>
                                <CodeBlock className="dark-code-block" code={`
curl https://api.mocks.cloud/api/mocks -X POST \\
    -H "Authorization: Bearer $CLOUDMOCKS_KEY"
                                `}/>
                                <p>Sample output:</p>
                                <CodeBlock className="dark-code-block" code={`
{
  "mockId" : "1c29de2267714a048b33c1d3ddba959c",
  "status" : "DRAFT"
}
                                `}/>
                                <blockquote>
                                    <p>
                                        <i className="icon icon-info-with-circle m-r-xs"/>Once you have created a mock,
                                        ensure you capture the <code>mockId</code>.
                                    </p>
                                    <p>
                                        In the following examples, we assume you have set the environment variable <code>MOCK_ID</code> as follows:
                                    </p>
                                    <CodeBlock className="m-t-sm" code={`
export MOCK_ID="1c29de2267714a048b33c1d3ddba959c"
                                    `}/>
                                    </blockquote>
                                <h4 className="m-t-md">Upload file</h4>
                                <p>
                                    This API enables you to upload <Link to="/docs/mocks/simple">OpenAPI specification files</Link> and <Link
                                    to="/docs/mocks/advanced">script files</Link>.
                                </p>
                                <CodeBlock className="dark-code-block" code={`
curl https://api.mocks.cloud/api/mocks/$MOCK_ID/spec -F "data=@./openapi.yaml" \\
    -H "Authorization: Bearer $CLOUDMOCKS_KEY"
                                `}/>
                                <blockquote>
                                    <p>
                                        <i className="icon icon-info-with-circle m-r-xs"/>In this example, we are uploading
                                        the file named <code>openapi.yaml</code> from the current directory.
                                    </p>
                                    <p>
                                        <strong>Important:</strong> do not forget to provision your mock if it is in
                                        the <code>DRAFT</code> status. Draft mocks are not available to access.
                                    </p>
                                </blockquote>

                                <h4 className="m-t-md">Provision mock</h4>
                                <p>
                                    To activate a mock, you must <em>provision</em> it. This API enables you
                                    to trigger the <em>provision</em> action for a mock.
                                </p>
                                <CodeBlock className="dark-code-block" code={`
curl https://api.mocks.cloud/api/mocks/$MOCK_ID/actions/provision -X POST \\
    -H "Authorization: Bearer $CLOUDMOCKS_KEY"
                                `}/>
                                <blockquote>
                                    Provisioning can take a few minutes. You can check on progress by using
                                    the status API or provisioning events API.
                                </blockquote>

                                <h4 className="m-t-md">Get mock status</h4>
                                <p>
                                    A mock can have a variety of statuses, including <code>ACTIVE</code>, <code>DRAFT</code> and <code>FAILED</code>.
                                    Obtain the current status of a mock by using this API.
                                </p>
                                <CodeBlock className="dark-code-block" code={`
curl https://api.mocks.cloud/api/mocks/$MOCK_ID/status \\
    -H "Authorization: Bearer $CLOUDMOCKS_KEY"
                                `}/>
                                <p>Sample output:</p>
                                <CodeBlock className="dark-code-block" code={`
{
  "status" : "DRAFT",
  "lastModified" : 1641520928292
}
                                `}/>
                                <blockquote>
                                    Note: Timestamps are in Unix epoch millisecond format.
                                </blockquote>

                                <h4 className="m-t-md">Get mock endpoint</h4>
                                <p>
                                    Once a mock has <code>ACTIVE</code> status, fetch its endpoint. This is the URL
                                    on which you can call its operations.
                                </p>
                                <CodeBlock className="dark-code-block" code={`
curl https://api.mocks.cloud/api/mocks/$MOCK_ID/endpoint \\
    -H "Authorization: Bearer $CLOUDMOCKS_KEY"
                                `}/>
                                <p>Sample output:</p>
                                <CodeBlock className="dark-code-block" code={`
{
  "baseUrl" : "https://live.mocks.cloud/8S54O5AM5STTJGMQNAJEPK34N99B648F",
  "specUrl" : "https://live.mocks.cloud/spec/?tenant=8S54O5AM5STTJGMQNAJEPK34N99B648F"
}
                                `}/>

                                <h4 className="m-t-md">Get provisioning events</h4>
                                <p>
                                    As a mock moves through the provisioning process,
                                    it emits events. These can be helpful to troubleshoot
                                    issues if a mock is unavailable or fails to provision.
                                </p>
                                <CodeBlock className="dark-code-block" code={`
curl https://api.mocks.cloud/api/mocks/$MOCK_ID/events \\
    -H "Authorization: Bearer $CLOUDMOCKS_KEY"
                                `}/>
                                <p>Sample output</p>
                                <CodeBlock className="dark-code-block" code={`
[ {
  "occurred" : 1641344414223,
  "description" : "Transitioned to Running from Starting due to OnStartupSucceeded."
}, {
  "occurred" : 1641344412726,
  "description" : "Transitioned to Starting from Provisioned due to OnStarting."
}, {
  "occurred" : 1641344410300,
  "description" : "Transitioned to Provisioned from StartedProvisioning due to OnProvisioningSucceeded."
} ]
                                `}/>
                                <blockquote>
                                    <i className="icon icon-info-with-circle m-r-xs"/>Timestamps are in Unix epoch millisecond format.
                                </blockquote>

                                <h4 className="m-t-md">List all mocks</h4>
                                <p>
                                    This API lists all mocks, including their ID and status.
                                </p>
                                <CodeBlock className="dark-code-block" code={`
curl https://api.mocks.cloud/api/mocks \\
    -H "Authorization: Bearer $CLOUDMOCKS_KEY"
                                `}/>
                                <p>Sample output:</p>
                                <CodeBlock className="dark-code-block" code={`
[ {
  "mockId" : "default",
  "status" : "ACTIVE"
}, {
  "mockId" : "1c29de2267714a048b33c1d3ddba959c",
  "status" : "DRAFT"
} ]
                                `}/>

                                <h4 className="m-t-md">List uploaded files</h4>
                                <p>
                                    This API lists files you have uploaded to a mock.
                                </p>
                                <CodeBlock className="dark-code-block" code={`
curl https://api.mocks.cloud/api/mocks/$MOCK_ID/files \\
    -H "Authorization: Bearer $CLOUDMOCKS_KEY"
                                `}/>
                                <p>Sample output:</p>
                                <CodeBlock className="dark-code-block" code={`
[
  "backend.yaml"
]
                                `}/>
                            </div>
                        </div>

                    </div>
                </PageBlock>

                <Footer/>
            </div>
        );
    }
}

export default API;



// WEBPACK FOOTER //
// ./src/modules/views/docs/mocks/API.js